'use client';

import type React from 'react';
import { useEffect } from 'react';
import { Menu, Button } from 'antd';
import { MenuFoldOutlined, MenuUnfoldOutlined } from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import type { MenuItemType } from 'types';
import { sidebarItems, sidebarItemsConst } from 'constant';
import { useHoverState, getIconWithState } from 'hooks/useIconState';
import { filterMenuByPermission } from 'utils/permission';
import useAuthContext from 'stores/auth-context';

const { SubMenu } = Menu;
interface SidebarProps {
  collapsed: boolean;
  setCollapsed: React.Dispatch<React.SetStateAction<boolean>>;
}
const Sidebar: React.FC<SidebarProps> = ({ setCollapsed, collapsed }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { hoveredKey, handleMouseEnter, handleMouseLeave } = useHoverState();
  const { profile } = useAuthContext();
  useEffect(() => {
    const checkScreenSize = () => {
      const isMobileScreen = window.innerWidth < 1024;
      if (isMobileScreen) {
        setCollapsed(true);
      }
    };

    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  // Find the currently active keys based on path
  const findOpenKeys = () => {
    const pathSnippets = location.pathname.split('/').filter((i) => i);
    return pathSnippets.map((_, index) => '/' + pathSnippets.slice(0, index + 1).join('/'));
  };

  const openKeys = findOpenKeys();
  const selectedKeys = [location.pathname];

  const handleClick = (e: { key: string }) => {
    navigate(e.key);
    setCollapsed(true);
  };

  const toggleCollapsed = () => {
    setCollapsed(!collapsed);
  };

  const renderMenuItems = (items: MenuItemType[]) => {
    return items.map((item) => {
      const itemKey = item.path || item.key;
      const isSelected = selectedKeys.includes(itemKey);
      const isHovered = hoveredKey === itemKey;

      const icon = getIconWithState(
        item.icon as React.ReactElement,
        isSelected,
        isHovered,
        'bold', // selectedWeight
        'bold', // hoveredWeight
        'regular', // normalWeight
        'var(--primaryColor)', // color
      );

      if (item.children && item.children.length > 0) {
        return (
          <SubMenu
            className='!text-[14px] flex flex-row items-center'
            key={itemKey}
            icon={icon}
            title={collapsed ? '' : item.label}
            onMouseEnter={() => handleMouseEnter(itemKey)}
            onMouseLeave={handleMouseLeave}
          >
            {renderMenuItems(item.children)}
          </SubMenu>
        );
      }

      return (
        <Menu.Item
          className='!flex items-center justify-center !text-[14px] !leading-[20px] !m-0 !w-full !rounded-none !h-[32px] !mb-3'
          key={itemKey}
          icon={icon}
          onMouseEnter={() => handleMouseEnter(itemKey)}
          onMouseLeave={handleMouseLeave}
        >
          {collapsed ? '' : item.label}
        </Menu.Item>
      );
    });
  };

  return (
    <>
      {!collapsed && (
        <div className='fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden' onClick={() => setCollapsed(true)} />
      )}

      <div
        className={`
        ${collapsed ? 'w-[80px]' : 'w-[240px]'} 
        ${collapsed ? '-translate-x-full' : 'translate-x-0'}
        flex flex-col min-h-[calc(100vh-160px)] py-2 px-0 bg-white shadow-sm sidebar flex-shrink-0 rounded-lg
        transition-all duration-300 ease-in-out
        relative translate-x-0
      `}
      >
        <div
          className={`flex ${collapsed ? 'justify-center' : 'justify-end'} items-center p-2 border-b cursor-pointer`}
          onClick={toggleCollapsed}
        >
          <p
            className='text-[14px] leading-[20px] transition-all duration-300 ease-in-out inline-block'
            hidden={collapsed}
          >
            Nhấn để thu gọn sidebar
          </p>
          <Button
            type='text'
            icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
            className='!w-8 !h-8 !p-0 flex items-center justify-center'
          />
        </div>

        <Menu
          mode='inline'
          selectedKeys={selectedKeys}
          defaultOpenKeys={collapsed ? [] : openKeys}
          onClick={handleClick}
          className='flex-1 border-r-0 pt-3'
          inlineCollapsed={collapsed}
        >
          {sidebarItems.children &&
            renderMenuItems(filterMenuByPermission(sidebarItems.children, profile?.data?.permissionKeys || []))}
        </Menu>
        <Menu
          mode='inline'
          selectedKeys={selectedKeys}
          defaultOpenKeys={collapsed ? [] : openKeys}
          onClick={handleClick}
          className='h-[90px] border-r-0'
          inlineCollapsed={collapsed}
        >
          {renderMenuItems(sidebarItemsConst)}
        </Menu>
      </div>
    </>
  );
};

export default Sidebar;
